import type {
  CoachingConfig,
  CoachingPlan,
  CoachingSession,
} from '@efdigital/ll-addon-data';
import type { CoachCoacheePii } from '@efdigital/ll-cdk-coaching-sessions-api-types';

/** Everything a coach-facing `SessionCard` needs for one session. */
export type CoachSessionCardItem = {
  session: CoachingSession;
  coachingPlan: CoachingPlan;
  coachingConfig: CoachingConfig;
  coachee?: CoachCoacheePii;
};

/** The slice of the coach service this needs, so callers can pass a test double. */
type CoacheeDirectory = {
  getCoachees: () => Promise<CoachCoacheePii[]>;
};

/**
 * Resolves the plan, config, and coachee name each session card renders.
 *
 * The relationships are walked here rather than in the template because
 * `coachingPlan` and its `coaching`/`coachingConfig` are async; the session
 * queries include them, so this resolves from the store without extra requests.
 */
export async function toCoachSessionCardItems(
  sessions: CoachingSession[],
  coachees: CoacheeDirectory
): Promise<CoachSessionCardItem[]> {
  const coacheeNames = await coachees.getCoachees();
  const items: CoachSessionCardItem[] = [];

  for (const session of sessions) {
    const coachingPlan = (await session.coachingPlan)!;
    const coaching = (await coachingPlan.coaching)!;
    const coachingConfig = (await coaching.coachingConfig)!;

    items.push({
      session,
      coachingPlan,
      coachingConfig,
      coachee: coacheeNames.find((coachee) => coachee.userId === session.user),
    });
  }

  return items;
}
